import r from '../Utils/fetch'

const w: any = window
const endPoint = `${w.API_BILLING_URL}`

/**
 * Les dates du contrat d'un compte Entreprise.
 *
 * Elles découpent son année de RÉFÉRENCE : celle sur laquelle on relève sa
 * consommation Store et Track pour l'ajustement commercial annuel. ⚠️ Rien n'y
 * est facturé, et aucun écran qui la montre n'affiche d'inclus ni de montant :
 * l'inclus des produits est mensuel et ne concerne que le Pay as you go.
 * L'anniversaire est le jour et le mois du début — pas de champ à part.
 *
 * ⚠️ Toutes les dates sont des chaînes « YYYY-MM-DD », jamais des `Date`. Un
 * `new Date('2026-03-12')` est interprété en UTC puis affiché en heure locale :
 * à l'ouest de Greenwich, la date de fin d'un contrat recule d'un jour à
 * l'écran, pour certains utilisateurs seulement.
 */
export interface IContractDates {
	billingMode: 'contract' | 'stripe'
	/** Début du contrat. Vide = aucune date saisie. */
	startAt?: string
	/** Dernière journée COUVERTE, incluse. Vide = le contrat court sans terme. */
	endAt?: string
	/** Début de l'année de référence en cours. */
	yearStart?: string
	/** Dernière journée de cette année, incluse — déjà décalée par le back. */
	yearLastDay?: string
	/** Durée de la période en jours : une dernière année coupée fait moins d'un an. */
	yearDays?: number
	/** Contrat terminé : les droits produit du compte sont fermés. */
	ended: boolean
	/** Début dans le futur. Les droits restent ouverts. */
	notStarted: boolean
}

export const getContractDates = (billingAccountId: string): Promise<IContractDates> =>
	r({ url: `${endPoint}/v1/admin/billing-accounts/${billingAccountId}/contract`, method: 'GET' })

/**
 * Écrit les dates. Un champ absent ne bouge pas, un champ vide s'efface — d'où
 * les `undefined` plutôt que des chaînes vides par défaut.
 */
export const setContractDates = (
	billingAccountId: string,
	dates: { startAt?: string; endAt?: string }
): Promise<IContractDates> =>
	r({
		url: `${endPoint}/v1/admin/billing-accounts/${billingAccountId}/contract`,
		method: 'PUT',
		data: dates,
	})

/** « 2026-03-12 » → « 12/03/2026 ». Découpage de chaîne : aucun objet Date, aucun fuseau. */
export const formatDay = (iso?: string): string => {
	if (!iso) return ''
	const [y, m, d] = iso.split('-')
	return y && m && d ? `${d}/${m}/${y}` : iso
}

// ---- Consommation sur une année contractuelle ----

/**
 * Ce qu'un compte sous contrat a consommé sur une année.
 *
 * ⚠️ Ce n'est PAS une facture : aucun dépassement n'est calculé pour un compte
 * sous contrat, et il n'y a donc ni inclus ni montant facturé. Quelqu'un lit ces
 * volumes une fois par an et négocie l'ajustement.
 */
export interface IContractUsage {
	/** Années sélectionnables, la plus récente d'abord (« 2026-03-28 »). */
	periods: string[]
	period: string
	periodStart?: string
	/** Dernière journée couverte, incluse — déjà décalée par le back. */
	periodLastDay?: string
	periodDays: number
	/** Année en cours : les volumes ne sont que partiels. */
	ongoing: boolean
	projects: IContractProjectUsage[]
	servers: IContractServerUsage[]
	/** ⚠️ Admin seulement : ce que Quanti paie à Google. Absent côté client. */
	totalCostEur?: number
}

export interface IContractProjectUsage {
	folderId: string
	name: string
	/** Cumul sur la période : l'analyse est un flux. */
	analysisGb: number
	/** MOYENNE sur les mois écoulés : le stockage est un stock, pas un flux. */
	storageAvgGb: number
	services: IContractServiceUsage[]
	/** À comparer à periodDays : un écart dit « pas mesuré », pas « rien consommé ». */
	measuredDays: number
}

export interface IContractServiceUsage {
	service: string
	/** ⚠️ Admin seulement. */
	costEur?: number
}

export interface IContractServerUsage {
	id: string
	domain: string
	/** ⚠️ null ≠ 0 : un serveur sans sidecar ne remonte aucun hit. */
	hits: number | null
}

export const getContractUsage = (
	billingAccountId: string,
	period?: string
): Promise<IContractUsage> =>
	r({
		url: `${endPoint}/v1/admin/billing-accounts/${billingAccountId}/contract-usage${
			period ? `?period=${period}` : ''
		}`,
		method: 'GET',
	})

/** « 8123.4 » → « 8,12 To » ; en dessous de 1024 Go, on reste en Go. */
export const formatGb = (gb: number): string =>
	gb >= 1024
		? `${(gb / 1024).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} To`
		: `${gb.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} Go`

/** « 18400000 » → « 18,4 M ». null se dit « non mesuré », jamais « 0 ». */
export const formatHits = (hits: number | null): string => {
	if (hits === null || hits === undefined) return 'non mesuré'
	if (hits >= 1_000_000) return `${(hits / 1_000_000).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} M`
	return hits.toLocaleString('fr-FR')
}

export const formatEur = (eur?: number): string =>
	eur === undefined || eur === null
		? ''
		: `${eur.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`

// ---- Taux de marge du datawarehouse ----

/**
 * Pose le taux de marge d'un compte, en FRACTION (0,25 = 25 %).
 * `null` rétablit le taux par défaut de la plateforme.
 *
 * ⚠️ Ne vaut que pour les journées mesurées ENSUITE : le taux est recopié sur
 * chaque ligne de mesure à sa création et n'y bouge plus. Modifier ce champ ne
 * change donc jamais un montant déjà constaté — c'est voulu, et l'écran doit le
 * dire, sinon on l'utilisera en croyant corriger une facture.
 */
export interface IDwMargin {
	/** Taux propre au compte, en fraction. null = il suit la plateforme. */
	rate: number | null
	/** Taux par défaut de la plateforme. */
	platformRate: number
	/** Ce qui s'applique réellement. */
	effectiveRate: number
}

export const getDwMargin = (billingAccountId: string): Promise<IDwMargin> =>
	r({ url: `${endPoint}/v1/admin/billing-accounts/${billingAccountId}/dw-margin`, method: 'GET' })

export const setDwMarginRate = (billingAccountId: string, rate: number | null): Promise<any> =>
	r({
		url: `${endPoint}/v1/admin/billing-accounts/${billingAccountId}/dw-margin`,
		method: 'PUT',
		data: { rate },
	})
